import {
  US_CA_INTERNATIONAL_PHONE_NUMBER_PREFIX,
  US_CA_PHONE_NUMBER_MASK,
} from './constants';

function digitsOnly(value: string) {
  return value.replace(/\D/g, '');
}

/**
 * Processes the phone number input and returns only
 * the digits of the phone number without the leading US
 * and CA country code (1).
 *
 * @example
 * ```
 * nationalUsPhoneNumber('+1 (923) 456-7890') // '9234567890'
 * nationalUsPhoneNumber('(923) 456-7890') // '9234567890'
 * nationalUsPhoneNumber('1-800-456-7890') // '8004567890'
 * nationalUsPhoneNumber('92345678901') // '9234567890'
 * ```
 */
function nationalUsPhoneNumber(value: string) {
  return digitsOnly(value).replace(/^1/, '').slice(0, 10);
}

export function internationalUsPhoneNumber(value: string) {
  return `${US_CA_INTERNATIONAL_PHONE_NUMBER_PREFIX}${nationalUsPhoneNumber(value)}`;
}

export function formatNationalUsPhoneNumber(value: string) {
  return formatPartialDigitsWithMask(
    nationalUsPhoneNumber(value),
    US_CA_PHONE_NUMBER_MASK,
  );
}

/**
 * Formats a string of digits using a mask.
 * The formatted output will either be the same length as the mask
 * or stop at the last digit of the string of digits.
 *
 * @example
 * ```ts
 * formatPartialDigitsWithMask('2345678901', '(###) ###-####');
 * // '(234) 567-8901'
 *
 * formatPartialDigitsWithMask('23456', '(###) ###-####');
 * // '(234) 56'
 *
 * formatPartialDigitsWithMask('23456789011234', '(###) ###-####');
 * // '(234) 567-8901'
 *
 * formatPartialDigitsWithMask('23456789011234', '(###) ###-####', 'x');
 * // '(xxx) xxx-xxxx'
 *
 * formatPartialDigitsWithMask('123457887654321', 'xxxx xxxx xxxx xxxx', 'x');
 * // '1234 5788 7654 321'
 *
 * ```
 */
function formatPartialDigitsWithMask(
  stringOfDigits: string,
  mask: string,
  /**
   * Use a different character than '#' to indicate where the digits should be inserted.
   */
  maskChar = '#',
) {
  const digits = stringOfDigits.split('');
  const maskArray = mask.split('');

  const formatted: string[] = [];
  while (digits.length > 0 && mask.length > 0) {
    const curMaskChar = maskArray.shift() ?? '';
    if (curMaskChar === maskChar) {
      formatted.push(digits.shift() ?? '');
    } else {
      formatted.push(curMaskChar);
    }
  }

  return formatted.join('');
}
